import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { WorkflowService } from '@core/services/workflow';
import {
  WORKFLOW_FORM_FIELD_INPUT_TYPES,
  WorkflowFormField,
  WorkflowFormSubmissionExternal,
} from '@workflows/types';
import { TuiAccordion } from '@taiga-ui/kit';
import { TuiIcon } from '@taiga-ui/core';
import { environment } from '@environment/environment';

type SubmissionDisplay = {
  workflowName: string;
  workflowId: string;
  nodeName: string;
  nodeId: number;
  formTitle: string;
  formId: number;
  fields: FieldDisplay[];
  submittedAt: string | null;
  isValid: boolean;
};

type FieldDisplay = {
  key: string;
  title: string;
  type: string;
  value: any;
  displayValue: string;
  files?: Array<{
    id: number;
    filename: string;
    path: string;
    url: string;
  }>;
};

@Component({
  selector: 'x-workflow-submissions-info',
  standalone: true,
  imports: [CommonModule, TuiAccordion, TuiIcon],
  templateUrl: './workflow-submissions-info.component.html',
  styleUrl: './workflow-submissions-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowSubmissionsInfoComponent {
  constructor() {
    this.workflowService.refreshEntities();
  }

  private readonly workflowService = inject(WorkflowService);

  // Получаем данные из сервиса
  private readonly $workflowData = this.workflowService.$requestsWorkflowData;

  // Преобразуем данные для отображения
  public readonly $submissions: Signal<SubmissionDisplay[]> = computed(() => {
    const data = this.$workflowData();

    if (!data) return [];

    const { requestsWorkflows, workflowNodesForms, submissions } = data;

    return submissions
      .filter(s => s.data && s.submittedAt) // Только отправленные формы с данными
      .map(submission =>
        this.mapSubmissionToDisplay(
          submission,
          workflowNodesForms,
          requestsWorkflows,
        ),
      )
      .filter((item): item is SubmissionDisplay => item !== null)
      .sort(this.sortBySubmittedDate);
  });

  // Группируем сабмиты по регламентам
  public readonly $submissionsByWorkflow = computed(() => {
    const submissions = this.$submissions();
    const grouped = new Map<string, SubmissionDisplay[]>();

    submissions.forEach(sub => {
      const existing = grouped.get(sub.workflowId) || [];

      existing.push(sub);
      grouped.set(sub.workflowId, existing);
    });

    return Array.from(grouped.entries()).map(([workflowId, items]) => ({
      workflowId,
      workflowName: items[0].workflowName,
      submissions: items,
    }));
  });

  /**
   * Преобразует submission в формат для отображения
   */
  private mapSubmissionToDisplay(
    submission: WorkflowFormSubmissionExternal,
    forms: any[],
    workflows: any[],
  ): SubmissionDisplay | null {
    const form = forms.find(f => f.id === submission.workflowNodeFormId);

    if (!form) return null;

    // Экземпляр регламента — из самого submission, а не первый регламент с этой нодой
    const workflow = workflows.find(w => w.id === submission.requestWorkflowId);
    const node = workflow?.nodes.find((n: any) => n.id === form.workflowNodeId);

    if (!workflow || !node) return null;

    const allFields = form.schema?.groups?.flatMap((g: any) => g.fields) || [];
    const fields = this.mapFieldsToDisplay(
      allFields,
      submission.data || {},
      submission.files || [],
    );

    return {
      workflowName: workflow.workflowVersion.workflow.name,
      workflowId: workflow.id,
      nodeName: node.title,
      nodeId: node.id,
      formTitle: form.title,
      formId: form.id,
      fields,
      submittedAt: submission.submittedAt || null,
      isValid: submission.isValid ?? false,
    };
  }

  /**
   * Сортирует submissions по дате отправки (новые первыми)
   */
  private sortBySubmittedDate(
    a: SubmissionDisplay,
    b: SubmissionDisplay,
  ): number {
    if (!a.submittedAt) return 1;
    if (!b.submittedAt) return -1;

    return (
      new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    );
  }

  /**
   * Преобразует поля формы в удобный формат для отображения
   */
  private mapFieldsToDisplay(
    fields: WorkflowFormField[],
    submissionData: Record<string, { value: any }>,
    files: WorkflowFormSubmissionExternal['files'] = [],
  ): FieldDisplay[] {
    const inputTypes = Object.values(WORKFLOW_FORM_FIELD_INPUT_TYPES);

    return fields
      .filter(field => inputTypes.includes(field.type as any))
      .map(field => this.createFieldDisplay(field, submissionData, files))
      .filter(field => this.shouldShowField(field));
  }

  /**
   * Создает объект FieldDisplay из поля формы
   */
  private createFieldDisplay(
    field: WorkflowFormField,
    submissionData: Record<string, { value: any }>,
    files: WorkflowFormSubmissionExternal['files'],
  ): FieldDisplay {
    const fieldValue = submissionData[field.key]?.value;
    const fieldFiles = this.mapFieldFiles(files, field.key);

    return {
      key: field.key,
      title: field.title || field.key,
      type: field.type,
      value: fieldValue,
      displayValue: this.formatFieldValue(field.type, fieldValue),
      files: fieldFiles,
    };
  }

  /**
   * Маппит файлы для конкретного поля
   */
  private mapFieldFiles(
    files: WorkflowFormSubmissionExternal['files'],
    fieldKey: string,
  ) {
    if (!files) return [];

    return files
      .filter(f => f.fieldKey === fieldKey && f.logisticRequestFile)
      .map(f => ({
        id: f.id,
        filename: f.logisticRequestFile!.originFilename,
        path: f.logisticRequestFile!.path,
        url: environment.staticUrl + f.logisticRequestFile!.path,
      }));
  }

  /**
   * Определяет, нужно ли показывать поле
   */
  private shouldShowField(field: FieldDisplay): boolean {
    const hasValue = field.value !== null && field.value !== undefined;
    const hasFiles = !!(field.files && field.files.length > 0);

    return hasValue || hasFiles;
  }

  /**
   * Форматирует значение поля для отображения
   */
  private formatFieldValue(fieldType: string, value: any): string {
    if (value === null || value === undefined) return '—';

    switch (fieldType) {
      case 'inputCheckbox':
        return value ? '✓ Да' : '✗ Нет';

      case 'inputButton':
        return value ? '✓' : '';

      case 'inputDate':
        return this.formatDate(value);

      case 'inputDatetime':
        return this.formatDateTime(value);

      case 'inputMultiselect':
        return Array.isArray(value) ? value.join(', ') : String(value);

      case 'inputPhotos':
      case 'inputFiles':
        return ''; // Файлы отображаются отдельно

      case 'inputSelect':
      case 'inputRadio':
      case 'inputNumber':
      case 'inputString':
      case 'inputTextarea':
        return String(value);

      default:
        return String(value);
    }
  }

  /**
   * Форматирует дату
   */
  private formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Форматирует дату и время
   */
  public formatDateTime(dateString: string): string {
    try {
      return new Date(dateString).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Проверяет, является ли файл изображением
   */
  isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  }
}
